/** @format */

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../../api.ts';
import ProductSearchAutocomplete from './ProductSearchAutocomplete';
import { formatApiError } from './productName';

function kitLabel(kit) {
  return kit.prefixed_internal_name || kit.public_name || `Kit #${kit.id}`;
}

function messageFromApi(err, fallback) {
  const apiError = err?.response?.data?.error;
  if (typeof apiError === 'string' && apiError) return apiError;
  return formatApiError(err, fallback);
}

function ProductContainingKitsPanel({
  productId,
  productName,
  writesDisabled,
  onCountChange,
  onSyncingChange,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [replacement, setReplacement] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [odooProgress, setOdooProgress] = useState(null);

  const loadKits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/products/v2/admin-products/${productId}/containing-kits/`,
      );
      const results = response.data?.results || [];
      setKits(results);
      onCountChange?.(results.length);
      setSelectedIds((current) => {
        const valid = new Set(results.map((kit) => kit.id));
        return new Set([...current].filter((id) => valid.has(id)));
      });
    } catch (err) {
      setError(messageFromApi(err, 'Impossible de charger les kits.'));
      setKits([]);
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [onCountChange, productId]);

  useEffect(() => {
    loadKits();
  }, [loadKits]);

  const filteredKits = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return kits;
    return kits.filter((kit) => {
      const internal = (kit.prefixed_internal_name || '').toLowerCase();
      const pub = (kit.public_name || '').toLowerCase();
      return (
        internal.includes(query) ||
        pub.includes(query) ||
        String(kit.id).includes(query)
      );
    });
  }, [filter, kits]);

  const filteredIds = filteredKits.map((kit) => kit.id);
  const selectedVisibleCount = filteredIds.filter((id) =>
    selectedIds.has(id),
  ).length;
  const allVisibleSelected =
    filteredIds.length > 0 && selectedVisibleCount === filteredIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  const selectedKits = useMemo(
    () => kits.filter((kit) => selectedIds.has(kit.id)),
    [kits, selectedIds],
  );
  const kitsThatWouldBeEmpty = selectedKits.filter(
    (kit) => kit.kit_line_count <= 1,
  );

  const replacementId = replacement?.id ?? null;
  const replacementIsSelf =
    replacementId != null && Number(replacementId) === Number(productId);
  const replacementName =
    replacement?.answer ||
    replacement?.prefixed_internal_name ||
    replacement?.public_name ||
    '';

  const toggleAllVisible = () => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (allVisibleSelected) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleOne = (kitId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(kitId)) next.delete(kitId);
      else next.add(kitId);
      return next;
    });
  };

  const syncKitsToOdoo = async (kits) => {
    onSyncingChange?.(true);
    const failed = [];
    let completed = 0;
    setOdooProgress({
      total: kits.length,
      completed: 0,
      currentName: kits[0]?.name || '',
      failed: [],
    });
    try {
      for (let index = 0; index < kits.length; index += 1) {
        const kit = kits[index];
        setOdooProgress({
          total: kits.length,
          completed,
          currentName: kit.name,
          failed: [...failed],
        });
        try {
          await api.post(
            `/products/v2/admin-products/${kit.id}/sync_to_odoo/`,
            {},
            {},
          );
        } catch (err) {
          failed.push({
            name: kit.name,
            error: messageFromApi(err, 'La synchronisation Odoo a échoué.'),
          });
        }
        completed += 1;
        setOdooProgress({
          total: kits.length,
          completed,
          currentName: kit.name,
          failed: [...failed],
        });
      }
    } finally {
      setOdooProgress(null);
      onSyncingChange?.(false);
    }

    if (failed.length === 0) {
      enqueueSnackbar(
        kits.length > 1
          ? `${kits.length} kits synchronisés avec Odoo.`
          : '1 kit synchronisé avec Odoo.',
        { variant: 'success' },
      );
    } else {
      enqueueSnackbar(
        `Synchronisation Odoo en échec pour : ${failed
          .map((kit) => kit.name)
          .join(', ')}`,
        { variant: 'warning' },
      );
    }
  };

  const applyAction = async () => {
    if (!confirmAction || selectedKits.length === 0) return;
    setSubmitting(true);
    try {
      const response = await api.post(
        `/products/v2/admin-products/${productId}/update-containing-kits/`,
        {},
        {
          kit_ids: selectedKits.map((kit) => kit.id),
          action: confirmAction,
          ...(confirmAction === 'replace'
            ? { replacement_product_id: replacementId }
            : {}),
        },
      );
      const data = response.data || {};
      const parts = [];
      if (data.updated) {
        parts.push(
          data.updated > 1
            ? `${data.updated} kits mis à jour`
            : '1 kit mis à jour',
        );
      }
      if (data.merged) {
        parts.push(
          data.merged > 1
            ? `${data.merged} kits fusionnés (quantités additionnées)`
            : '1 kit fusionné (quantités additionnées)',
        );
      }
      if (data.removed) {
        parts.push(
          data.removed > 1
            ? `Produit retiré de ${data.removed} kits`
            : 'Produit retiré d’1 kit',
        );
      }
      enqueueSnackbar(parts.join('. ') || 'Aucune modification.', {
        variant: 'success',
      });
      if (data.emptied_kits?.length) {
        enqueueSnackbar(
          `Ces kits ne sont plus des kits : ${data.emptied_kits
            .map((kit) => kit.name)
            .join(', ')}`,
          { variant: 'warning' },
        );
      }
      setConfirmAction(null);
      setSelectedIds(new Set());
      const kitsToSync = data.kits_to_sync || [];
      await loadKits();
      setSubmitting(false);
      if (kitsToSync.length > 0) {
        await syncKitsToOdoo(kitsToSync);
      }
    } catch (err) {
      enqueueSnackbar(messageFromApi(err, 'Impossible de modifier les kits.'), {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || Boolean(odooProgress);
  const odooPercent = odooProgress
    ? Math.round((odooProgress.completed / odooProgress.total) * 100)
    : 0;

  const previewNames = selectedKits.slice(0, 12);
  const hiddenNameCount = selectedKits.length - previewNames.length;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Kits du catalogue qui contiennent ce produit. Les modifications sont
        appliquées tout de suite, sans enregistrer la fiche produit.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <TextField
          size="small"
          label="Filtrer les kits"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          sx={{ minWidth: 220, flex: '0 0 240px' }}
        />
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <ProductSearchAutocomplete
            label="Remplacer par"
            onChange={setReplacement}
            disabled={writesDisabled || busy}
          />
          {replacementIsSelf && (
            <Typography variant="caption" color="error">
              Choisissez un autre produit que celui en cours d’édition.
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          disabled={
            writesDisabled ||
            busy ||
            selectedKits.length === 0 ||
            !replacementId ||
            replacementIsSelf
          }
          onClick={() => setConfirmAction('replace')}
          sx={{ mt: 1 }}
        >
          Remplacer
        </Button>
        <Button
          variant="outlined"
          color="error"
          disabled={writesDisabled || busy || selectedKits.length === 0}
          onClick={() => setConfirmAction('remove')}
          sx={{ mt: 1 }}
        >
          Retirer
        </Button>
      </Box>

      <Typography variant="body2" sx={{ mb: 1 }}>
        {selectedKits.length === 0
          ? 'Aucun kit sélectionné'
          : selectedKits.length === 1
          ? '1 kit sélectionné'
          : `${selectedKits.length} kits sélectionnés`}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : kits.length === 0 ? (
        <Typography variant="body1" sx={{ py: 4 }}>
          Ce produit n’est dans aucun kit.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={toggleAllVisible}
                    disabled={filteredKits.length === 0}
                    inputProps={{ 'aria-label': 'Tout sélectionner' }}
                  />
                </TableCell>
                <TableCell>Nom interne</TableCell>
                <TableCell>Nom public</TableCell>
                <TableCell align="right">Quantité</TableCell>
                <TableCell>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredKits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    Aucun kit ne correspond à ce filtre.
                  </TableCell>
                </TableRow>
              ) : (
                filteredKits.map((kit) => (
                  <TableRow key={kit.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.has(kit.id)}
                        onChange={() => toggleOne(kit.id)}
                        inputProps={{
                          'aria-label': `Sélectionner ${kitLabel(kit)}`,
                        }}
                      />
                    </TableCell>
                    <TableCell>{kit.prefixed_internal_name || '—'}</TableCell>
                    <TableCell>{kit.public_name || '—'}</TableCell>
                    <TableCell align="right">{kit.quantity ?? '—'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          label={kit.is_active ? 'Actif' : 'Inactif'}
                          color={kit.is_active ? 'success' : 'default'}
                        />
                        <Chip
                          size="small"
                          label={kit.is_validated ? 'Validé' : 'Non validé'}
                          color={kit.is_validated ? 'primary' : 'default'}
                          variant={kit.is_validated ? 'filled' : 'outlined'}
                        />
                        {kit.is_archived && (
                          <Chip size="small" label="Archivé" />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => {
          if (!submitting) setConfirmAction(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmAction === 'replace'
            ? 'Remplacer le produit'
            : 'Retirer le produit'}
        </DialogTitle>
        <DialogContent>
          {confirmAction === 'replace' ? (
            <Typography variant="body1" sx={{ mb: 2 }}>
              Remplacer « {productName} » par « {replacementName} » dans{' '}
              {selectedKits.length} kit{selectedKits.length > 1 ? 's' : ''} ? Si
              le produit de remplacement est déjà dans un kit, les quantités
              seront additionnées.
            </Typography>
          ) : (
            <Typography variant="body1" sx={{ mb: 2 }}>
              Retirer « {productName} » de {selectedKits.length} kit
              {selectedKits.length > 1 ? 's' : ''} ?
            </Typography>
          )}
          {confirmAction === 'remove' && kitsThatWouldBeEmpty.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {kitsThatWouldBeEmpty.length === 1
                ? `« ${kitLabel(
                    kitsThatWouldBeEmpty[0],
                  )} » n’aura plus de ligne et ne sera plus un kit.`
                : `Ces kits n’auront plus de ligne et ne seront plus des kits : ${kitsThatWouldBeEmpty
                    .map(kitLabel)
                    .join(', ')}.`}
            </Alert>
          )}
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {previewNames.map((kit) => (
              <Typography component="li" variant="body2" key={kit.id}>
                {kitLabel(kit)}
                {kit.quantity != null ? ` — quantité ${kit.quantity}` : ''}
              </Typography>
            ))}
          </Box>
          {hiddenNameCount > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              et {hiddenNameCount} autre{hiddenNameCount > 1 ? 's' : ''}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} disabled={submitting}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color={confirmAction === 'remove' ? 'error' : 'primary'}
            onClick={applyAction}
            disabled={submitting}
          >
            {submitting && <CircularProgress size={18} sx={{ mr: 1 }} />}
            {confirmAction === 'replace' ? 'Remplacer' : 'Retirer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(odooProgress)}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Synchronisation Odoo</DialogTitle>
        <DialogContent>
          {odooProgress && (
            <>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Le catalogue est à jour. Synchronisation de{' '}
                {Math.min(odooProgress.completed + 1, odooProgress.total)} sur{' '}
                {odooProgress.total}.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {odooProgress.currentName}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={odooPercent}
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                Chaque kit peut prendre plusieurs secondes. Laissez cette
                fenêtre ouverte.
              </Typography>
              {odooProgress.failed.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Échec pour :{' '}
                  {odooProgress.failed.map((kit) => kit.name).join(', ')}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

ProductContainingKitsPanel.propTypes = {
  productId: PropTypes.number.isRequired,
  productName: PropTypes.string,
  writesDisabled: PropTypes.bool,
  onCountChange: PropTypes.func,
  onSyncingChange: PropTypes.func,
};

ProductContainingKitsPanel.defaultProps = {
  productName: '',
  writesDisabled: false,
  onCountChange: null,
  onSyncingChange: null,
};

export default ProductContainingKitsPanel;
